/**
 * Poll Analytics Panel - Interface pour les analytics conversationnels
 *
 * Permet d'interroger les résultats d'un sondage en langage naturel
 * et affiche les insights automatiques générés par l'IA.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Send,
  Sparkles,
  TrendingUp,
  AlertCircle,
  Info,
  Loader2,
  RefreshCw,
  Lock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { pollAnalyticsService } from "@core/services/PollAnalyticsService";
import type {
  AnalyticsResponse,
  AutoInsight,
} from "@core/services/PollAnalyticsService";
import { useAnalyticsQuota } from "@core/hooks/useAnalyticsQuota";
import { logger } from "@core/lib/logger";
import { useToast } from "@core/hooks/use-toast";

interface Props {
  pollId: string;
  pollTitle: string;
  /** Produit hôte — adapte le vocabulaire (form → "formulaire"). Défaut date. */
  productType?: string;
  /** Types des questions présentes — adapte les "quick queries" proposées. */
  questionTypes?: string[];
}

export default function PollAnalyticsPanel({
  pollId,
  pollTitle,
  productType,
  questionTypes,
}: Props) {
  const { t } = useTranslation("polls");
  // Vocabulaire adapté au produit hôte : sur DooQuest (formulaires), on parle
  // de "formulaire" et de "répondre", pas de "sondage"/"voter".
  const isForm = productType === "form";
  const howItWorksDesc = isForm
    ? "analytics.howItWorks.descriptionForm"
    : "analytics.howItWorks.description";
  const queryPlaceholder = isForm
    ? "analytics.queryPlaceholderForm"
    : "analytics.queryPlaceholder";
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<AnalyticsResponse | null>(null);
  const [insights, setInsights] = useState<AutoInsight[]>([]);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showInsights, setShowInsights] = useState(false); // Repliés par défaut
  const { toast } = useToast();
  const { quota, incrementQuota, checkQuota, getQuotaMessage } =
    useAnalyticsQuota();

  // Traduction des types d'insights
  const translateInsightType = (type: string): string => {
    const translations: Record<string, string> = {
      TREND: t("analytics.insightType.trend"),
      ANOMALY: t("analytics.insightType.anomaly"),
      SUMMARY: t("analytics.insightType.summary"),
      RECOMMENDATION: t("analytics.insightType.recommendation"),
    };
    return translations[type.toUpperCase()] || type;
  };

  const loadAutoInsights = useCallback(async () => {
    setLoadingInsights(true);
    try {
      const autoInsights =
        await pollAnalyticsService.generateAutoInsights(pollId);
      setInsights(autoInsights);
      logger.info("Auto insights loaded", "analytics", {
        count: autoInsights.length,
      });
    } catch (error) {
      logger.error("Failed to load auto insights", "analytics", { error });
      // Ne pas afficher de toast d'erreur pour les insights auto (non-bloquant)
    } finally {
      setLoadingInsights(false);
    }
  }, [pollId]);

  // Charger les insights automatiques au montage
  useEffect(() => {
    loadAutoInsights();
  }, [loadAutoInsights]);

  // Faire défiler jusqu'à la réponse dès qu'elle arrive : sinon, sur mobile ou
  // résultats longs, l'utilisateur ne voit pas que sa requête a abouti.
  const responseRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (response) {
      responseRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [response]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await runQuery(query);
  };

  // Lance une requête analytics pour une question donnée. Centralisé pour que
  // les "quick queries" déclenchent exactement le même chemin que le formulaire,
  // sans dépendre d'un dispatch DOM global ni d'une course sur le state `query`.
  const runQuery = async (question: string) => {
    const q = question.trim();
    if (!q || loading) return;

    // Vérifier le quota AVANT d'envoyer la requête
    if (!checkQuota()) {
      toast({
        title: t("analytics.quotaExhausted.title"),
        description: getQuotaMessage(),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      const result = await pollAnalyticsService.queryPoll({
        question: q,
        pollId,
        context: "summary",
      });

      // Incrémenter le quota UNIQUEMENT si ce n'est PAS du cache
      if (!result.cached) {
        const canContinue = incrementQuota();
        console.log(
          "Quota incrémenté, peut continuer:",
          canContinue,
          "Quota actuel:",
          quota,
        );
      } else {
        console.log("Réponse du cache - quota NON incrémenté");
      }

      setResponse(result);
      logger.info("Analytics query successful", "analytics", {
        query,
        cached: result.cached,
      });

      if (result.cached) {
        toast({
          title: t("analytics.cacheResponse.title"),
          description: t("analytics.cacheResponse.description"),
          duration: 2000,
        });
      }
    } catch (error) {
      logger.error("Analytics query failed", "analytics", { error });
      toast({
        title: t("analytics.queryError.title"),
        description: t("analytics.queryError.description"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickQuery = (quickQuery: string) => {
    setQuery(quickQuery);
    void runQuery(quickQuery);
  };

  // Quick queries adaptées aux types de questions présents : "l'option la plus
  // populaire" n'a de sens que s'il existe une question à choix (single/multiple).
  // Sinon on propose une exploration générique des réponses.
  const hasChoiceQuestion =
    !questionTypes ||
    questionTypes.some((tp) => tp === "single" || tp === "multiple");
  const quickQueries = [
    t("analytics.quickQuery.respondentCount"),
    hasChoiceQuestion
      ? t("analytics.quickQuery.mostPopularOption")
      : t("analytics.quickQuery.topQuestion"),
    t("analytics.quickQuery.interestingTrends"),
    t("analytics.quickQuery.summarizeResults"),
  ];

  const getInsightIcon = (type: AutoInsight["type"]) => {
    switch (type) {
      case "trend":
        return <TrendingUp className="w-5 h-5 text-blue-500" />;
      case "anomaly":
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case "summary":
        return <Info className="w-5 h-5 text-purple-500" />;
      case "recommendation":
        return <Sparkles className="w-5 h-5 text-green-500" />;
    }
  };

  const getInsightColor = (type: AutoInsight["type"]) => {
    switch (type) {
      case "trend":
        return "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20";
      case "anomaly":
        return "border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20";
      case "summary":
        return "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20";
      case "recommendation":
        return "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20";
    }
  };

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t("analytics.title")}
          </h2>
        </div>
        <button
          onClick={loadAutoInsights}
          disabled={loadingInsights}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          title={t("analytics.regenerateInsights")}
          data-testid="pollanalyticspanel-button"
        >
          <RefreshCw
            className={`w-4 h-4 ${loadingInsights ? "animate-spin" : ""}`}
          />
          {t("analytics.refresh")}
        </button>
      </div>

      {/* Bouton pour afficher/masquer les insights */}
      <button
        onClick={() => setShowInsights(!showInsights)}
        className="w-full flex items-center justify-between px-4 py-3 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors border border-purple-200 dark:border-purple-800"
        data-testid="pollanalyticspanel-button"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <span className="font-semibold text-purple-900 dark:text-purple-100">
            {t("analytics.autoInsights")}{" "}
            {insights.length > 0 && `(${insights.length})`}
          </span>
        </div>
        {showInsights ? (
          <ChevronUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-purple-600 dark:text-purple-400" />
        )}
      </button>

      {/* Insights automatiques (repliables) */}
      {showInsights && (
        <div className="space-y-4">
          {loadingInsights ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600 dark:text-purple-400" />
              <span className="ml-2 text-gray-600 dark:text-gray-300">
                {t("analytics.generatingInsights")}
              </span>
            </div>
          ) : insights.length > 0 ? (
            <div className="grid gap-3">
              {insights.map((insight, idx) => (
                <div
                  key={idx}
                  data-testid="insight-card"
                  className={`border-2 rounded-lg p-4 ${getInsightColor(insight.type)}`}
                >
                  <div className="flex items-start gap-3">
                    {getInsightIcon(insight.type)}
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        {insight.title}
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {insight.description}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t("analytics.confidence")}:{" "}
                          {Math.round(insight.confidence * 100)}%
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          •
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {translateInsightType(insight.type)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Info className="w-12 h-12 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
              <p>{t("analytics.noInsights")}</p>
              <p className="text-sm mt-1">{t("analytics.noInsightsHint")}</p>
            </div>
          )}
        </div>
      )}

      {!showInsights && (
        <button
          onClick={() => setShowInsights(true)}
          className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
          data-testid="pollanalyticspanel-afficher-les-insights-automatiques"
        >
          {t("analytics.showAutoInsights")}
        </button>
      )}

      {/* Query interface */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t("analytics.askQuestion")}
        </h3>

        {/* Quick queries */}
        <div className="flex flex-wrap gap-2">
          {quickQueries.map((q, idx) => (
            <button
              key={idx}
              data-testid="quick-query-button"
              onClick={() => handleQuickQuery(q)}
              className="px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-purple-400 dark:hover:border-purple-500 transition-colors"
            >
              {q}
            </button>
          ))}
        </div>

        {/* Query form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type="text"
              data-testid="analytics-query-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t(queryPlaceholder)}
              className="w-full px-4 py-3 pr-12 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-500 dark:focus:border-purple-400 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 outline-none transition-all"
              disabled={loading}
            />
            <button
              type="submit"
              data-testid="analytics-send-button"
              disabled={loading || !query.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>

        {/* Response */}
        {response && (
          <div
            ref={responseRef}
            data-testid="analytics-response"
            className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-2 border-purple-200 dark:border-purple-800 rounded-lg p-6"
          >
            <div className="flex items-start gap-3">
              <Sparkles className="w-6 h-6 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <p className="text-gray-900 dark:text-gray-100 leading-relaxed">
                  {response.answer}
                </p>

                {response.insights && response.insights.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-purple-200 dark:border-purple-700">
                    <p className="text-sm font-semibold text-purple-900 dark:text-purple-200 mb-2">
                      {t("analytics.additionalInsights")}
                    </p>
                    <ul className="space-y-1">
                      {response.insights.map((insight, idx) => (
                        <li
                          key={idx}
                          className="text-sm text-purple-800 dark:text-purple-300 flex items-start gap-2"
                        >
                          <span className="text-purple-400 dark:text-purple-500 mt-0.5">
                            •
                          </span>
                          <span>{insight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>
                    {t("analytics.confidence")}:{" "}
                    {Math.round(response.confidence * 100)}%
                  </span>
                  {response.cached && (
                    <>
                      <span>•</span>
                      <span className="text-green-600 dark:text-green-400">
                        {t("analytics.cachedResponse")}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info footer */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-200">
            <p className="font-semibold mb-1">
              {t("analytics.howItWorks.title")}
            </p>
            <p>{t(howItWorksDesc, { pollTitle })}</p>
          </div>
        </div>
      </div>

      {/* Quota indicator */}
      <div
        data-testid="quota-indicator"
        className={`border-2 rounded-lg p-3 ${quota.canQuery ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800" : "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800"}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {quota.canQuery ? (
              <Sparkles className="w-4 h-4 text-green-600 dark:text-green-400" />
            ) : (
              <Lock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            )}
            <span
              className={`text-sm font-medium ${quota.canQuery ? "text-green-900 dark:text-green-200" : "text-orange-900 dark:text-orange-200"}`}
            >
              {getQuotaMessage()}
            </span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t("analytics.quotaUsed", { used: quota.used, limit: quota.limit })}
          </div>
        </div>
        {!quota.canQuery && (
          <p className="text-xs text-orange-700 dark:text-orange-300 mt-2">
            {t("analytics.signInForMore")}
          </p>
        )}
      </div>
    </div>
  );
}
